import { PASSKEY_PRF_SALT } from "@passwordflow/crypto";
import { browserSupportsWebAuthn, startAuthentication } from "@simplewebauthn/browser";
import type { AuthenticationResponseJSON, PublicKeyCredentialRequestOptionsJSON } from "@simplewebauthn/browser";
import { logout, passkeySignIn, passkeyUnlockOptions, reauthOptions, reauthWithPasskey } from "../api/auth.js";
import { enablePasskeyUnlock, passkeyUnlockSetupOptions } from "../api/security.js";
import { bytesToB64 } from "../b64.js";
import type { CryptoClient } from "../cryptoClient.js";
import { AuthFlowError } from "./authFlow.js";
import { isDesktop } from "./platform.js";

/** Passkeys need a browser with WebAuthn; the desktop app's webview has none for this site. */
export function passkeyUnlockAvailable(): boolean {
  return !isDesktop() && typeof window !== "undefined" && browserSupportsWebAuthn();
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** The PRF output from the client extension results, if the authenticator produced one. */
export function prfOutput(clientExtensionResults: unknown): Uint8Array | undefined {
  if (!isRecord(clientExtensionResults) || !isRecord(clientExtensionResults["prf"])) {
    return undefined;
  }
  const results = clientExtensionResults["prf"]["results"];
  if (!isRecord(results)) {
    return undefined;
  }
  const first = results["first"];
  if (first instanceof ArrayBuffer) {
    return new Uint8Array(first);
  }
  if (ArrayBuffer.isView(first)) {
    return new Uint8Array(first.buffer, first.byteOffset, first.byteLength);
  }
  return undefined;
}

interface PrfAssertion {
  /** Safe to send: the extension results, and with them the PRF output, are removed. */
  readonly response: AuthenticationResponseJSON;
  readonly prfB64: string;
}

/** Runs the browser prompt with the PRF extension. The PRF output never leaves this browser. */
async function assertWithPrf(options: PublicKeyCredentialRequestOptionsJSON): Promise<PrfAssertion> {
  const response = await startAuthentication({
    optionsJSON: { ...options, extensions: { ...options.extensions, prf: { eval: { first: new Uint8Array(PASSKEY_PRF_SALT) } } } }
  });
  const prf = prfOutput(response.clientExtensionResults);
  if (prf === undefined || prf.length !== 32) {
    throw new AuthFlowError("passkey-prf", "Dieser Passkey kann den Tresor nicht entsperren: Gerät oder Browser unterstützen die nötige Erweiterung (PRF) nicht. Nutze dein Master-Passwort.");
  }
  const prfB64 = bytesToB64(prf);
  prf.fill(0);
  return { response: { ...response, clientExtensionResults: {} }, prfB64 };
}

/** Turns unlock on for one passkey: one more confirmation on the device yields the PRF output. */
export async function turnOnPasskeyUnlock(client: CryptoClient, passkeyId: string): Promise<void> {
  const assertion = await assertWithPrf(await passkeyUnlockSetupOptions(passkeyId));
  const keyEncB64 = await client.sealForPasskey(assertion.prfB64);
  await enablePasskeyUnlock(passkeyId, keyEncB64, assertion.response);
}

/**
 * Sign-in and unlock without email or master password. The passkey proves the account to the
 * server, and its PRF output opens the account keys the server hands back.
 */
export async function signInWithPasskey(client: CryptoClient): Promise<string> {
  const assertion = await assertWithPrf(await passkeyUnlockOptions());
  const result = await passkeySignIn(assertion.response);
  try {
    await client.openWithPasskey(assertion.prfB64, result.keyEncB64);
  } catch {
    await logout().catch((): void => undefined);
    throw new AuthFlowError("vault-unreadable", "Der Passkey öffnet die Kontoschlüssel nicht. Melde dich mit deinem Master-Passwort an und schalte das Entsperren mit diesem Passkey neu ein.");
  }
  return result.email;
}

/**
 * Renews an expired session with a passkey. The keys in the worker stay; the passkey must belong
 * to the account that is open, or the new session is ended again.
 */
export async function reconfirmWithPasskey(email: string): Promise<void> {
  const assertion = await assertWithPrf(await passkeyUnlockOptions());
  const result = await passkeySignIn(assertion.response);
  if (result.email !== email) {
    await logout().catch((): void => undefined);
    throw new AuthFlowError("passkey-account", "Dieser Passkey gehört zu einem anderen Konto.");
  }
}

/** Step-up check with any passkey of the account. No PRF: only the user verification matters here. */
export async function confirmWithPasskey(): Promise<void> {
  const response = await startAuthentication({ optionsJSON: await reauthOptions() });
  await reauthWithPasskey({ ...response, clientExtensionResults: {} });
}
